import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { Features } from '@ultrasnip/contracts';
import { type FormEvent, useState } from 'react';
import { z } from 'zod';
import { safeRedirectPath } from '@/app/router';
import { AuthLayout } from '@/components/shell/auth-layout';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { FormAlert, TextField } from '@/features/auth/form-parts';
import { authClient, authErrorMessage } from '@/lib/auth-client';
import { QueryKeys } from '@/lib/queries';

export const Route = createFileRoute('/_auth/two-factor')({
  beforeLoad: () => {
    if (!Features.twoFactor) throw redirect({ to: '/login' });
  },
  validateSearch: z.object({ redirect: z.string().max(2048).optional() }),
  component: TwoFactorScreen,
});

function TwoFactorScreen() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const verify = async (value: string) => {
    setError(null);
    setPending(true);
    const { error: verifyError } =
      mode === 'totp'
        ? await authClient.twoFactor.verifyTotp({ code: value, trustDevice })
        : await authClient.twoFactor.verifyBackupCode({ code: value.trim(), trustDevice });
    setPending(false);
    if (verifyError) {
      setCode('');
      setError(authErrorMessage(verifyError));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: QueryKeys.me });
    await navigate({ to: safeRedirectPath(redirect) });
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (code.trim().length < (mode === 'totp' ? 6 : 8)) {
      setError(mode === 'totp' ? 'Enter all 6 digits.' : 'Enter a full backup code.');
      return;
    }
    void verify(code);
  };

  return (
    <AuthLayout
      title="Two-factor check"
      subtitle={
        mode === 'totp' ? 'Enter the code from your authenticator app.' : 'Enter one of your backup codes.'
      }
      footer={
        <Link to="/login" className="link">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormAlert message={error} />
        {mode === 'totp' ? (
          <div className="flex">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={setCode}
              onComplete={(value: string) => void verify(value)}
              autoFocus
              inputMode="numeric"
              pattern="^[0-9]*$"
              aria-label="Authenticator code"
              disabled={pending}
            >
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <InputOTPSlot key={index} index={index} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
        ) : (
          <TextField
            label="Backup code"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        )}
        <div className="flex items-center gap-2.5">
          <Checkbox
            id="trust-device"
            checked={trustDevice}
            onCheckedChange={(checked) => setTrustDevice(checked === true)}
          />
          <Label htmlFor="trust-device" className="text-[0.8125rem] font-normal text-foreground">
            Trust this device for 30 days
          </Label>
        </div>
        <Button type="submit" disabled={pending} className="mt-1 h-9 w-full">
          {pending && <Spinner />}
          Verify
        </Button>
        <button
          type="button"
          onClick={() => {
            setMode(mode === 'totp' ? 'backup' : 'totp');
            setCode('');
            setError(null);
          }}
          className="self-start rounded text-[0.75rem] text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          {mode === 'totp' ? 'Use a backup code instead' : 'Use my authenticator app instead'}
        </button>
      </form>
    </AuthLayout>
  );
}
